import { Navigate, useParams } from 'react-router-dom'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useAppSelector } from '@/store/hooks'
import { selectTasksInitialized, selectTasksLoading, selectTasksCompletedInitialized } from '@/store/tasks/tasksSelectors'
import { selectProjectsLoading } from '@/store/projects/projectsSelectors'
import { taskPath } from '@/utils/taskPath'

// Back-compat for notification emails sent before the task-link format changed
// (commit 6c753bd). They point at `/:workspace/projects/:projectId/task/:taskId`
// where `:taskId` is the Firestore doc id — a route that no longer exists. Resolve
// it to the canonical `/:workspace/task/PREFIX-NUMBER` modal route. (SCD-563)
const LegacyTaskRedirect = () => {
    const { workspace: workspaceUrlId, projectId, taskId } = useParams<{
        workspace: string
        projectId: string
        taskId: string
    }>()
    const { workspace } = useWorkspace()
    const loading = useAppSelector(selectTasksLoading)
    const initialized = useAppSelector(selectTasksInitialized)
    const completedInitialized = useAppSelector(selectTasksCompletedInitialized)
    const projectsLoading = useAppSelector(selectProjectsLoading)
    const task = useAppSelector(state => (taskId ? state.tasks.byId[taskId] : undefined))

    // Wait for the right workspace + loaded tasks before resolving, like
    // WorkspaceTaskModalRoute — a deep link can target a non-active workspace that
    // switches asynchronously, and resolving too early would bounce to the wrong place.
    const workspaceMatchesUrl = !workspaceUrlId || workspace?.workspaceUrlId === workspaceUrlId
    if (!workspace || !workspaceMatchesUrl || loading || !initialized || projectsLoading) {
        return null
    }

    const path = task && taskPath({ workspaceUrlId, prefix: workspace.workspaceTaskPrefixId, number: task.number, pending: task.pending })
    if (path) {
        return <Navigate to={path} replace />
    }

    // Completed tasks load a beat later (deferred listener) — wait for them before
    // treating the id as unknown, so a legacy link to a completed task still resolves.
    if (!completedInitialized) return null

    // Unknown task id (deleted, or not in this workspace) — fall back to the project.
    return <Navigate to={`/${workspaceUrlId}/projects/${projectId ?? ''}`} replace />
}

export default LegacyTaskRedirect
